import { all, put, select, takeLatest } from 'redux-saga/effects';
import { getI18n } from 'react-i18next';
import { DashboardAPI } from '../../utils/API/DashboardAPI';
import {
	CREATE_DASHBOARD,
	DELETE_DASHBOARD,
	SAVE_DASHBOARD,
	SET_SELECTED_DASHBOARD, SUBSCRIBE_WS_TOPICS,
	WS_DATA_DASHBOARD,
	WS_DATA_PRESENTATION_PAGE,
} from '../constants/dashboard.constants';
import { setEditDashboard, setLoading, setSelectedDashboard } from '../actions/dashboard.actions';
import { selectDashboards, selectEditDashboard, selectSelectedDashboard } from '../selectors/dashboard.selector';
import { selectOptions } from '../../containers/PresentationPage/selectors';
import {selectCurrentUser} from "../selectors/app.selectors";
import _flatten from 'lodash/flatten';
import _uniq from 'lodash/uniq';
import {MyWebSocket} from "../../utils/MyWebSocket";
import Dashboard from "../../utils/models/Dashboard";
import {dataReceived, getData} from "../actions/app.actions";
import {APP_MOUNTED, WS_DATA_INIT} from "../constants/app.constants";
import {AUTH_SUCCESS} from "../../containers/LoginPage/constants";
import {getActivePage} from "../../utils/functions";
import { openSnackbar } from '../actions/snackbar.actions';
import { consoleWarn } from '../../utils/log';

const api = new DashboardAPI();

// V4 dashboards live in account storage and no event echoes a write, so the V4 workers update the list themselves.
function *v4DashboardFailed(verb, e) {
	consoleWarn(`Failed to ${verb} the V4 dashboard`, e);
	yield put(setLoading(false));
	yield put(openSnackbar(getI18n().t('error')));
}

function *loadV4Dashboards() {
	const user = yield select(selectCurrentUser());
	if (!user?.isV4Native) return;
	let dashboards = [];
	// Only the end-user shell shows dashboards.
	if (user.isUser && user.v4ScopedAccountId) {
		try {
			dashboards = yield api.getV4Dashboards(user.v4ScopedAccountId);
		} catch (e) {
			consoleWarn('Failed to load the V4 dashboards', e);
		}
	}
	// Always answer: the landing page spins until this slice is fetched.
	yield put(dataReceived('dashboards', dashboards));
}

function *createV4Dashboard(user, name) {
	try {
		const taken = (yield select(selectDashboards())).map(d => d.id).toArray();
		const dashboard = yield api.createV4Dashboard(user.v4ScopedAccountId, name, taken);
		yield put(dataReceived('dashboard', dashboard));
		yield put(setSelectedDashboard(dashboard.id));
	} catch (e) {
		yield* v4DashboardFailed('create', e);
	}
}

function *saveV4Dashboard(user, dashboard) {
	const previous = (yield select(selectDashboards())).find(d => +d.id === +dashboard.id);
	// Shown at once, because the page closes the editor without waiting for the write.
	yield put(dataReceived('dashboard', dashboard.toJS()));
	try {
		yield api.saveV4Dashboard(user.v4ScopedAccountId, dashboard);
	} catch (e) {
		if (previous) yield put(dataReceived('dashboard', previous.toJS()));
		// Hand the unsaved edits back, unless the user has moved on.
		const editing = yield select(selectEditDashboard());
		const selected = yield select(selectSelectedDashboard());
		if (!editing && +selected === +dashboard.id) yield put(setEditDashboard(dashboard));
		yield* v4DashboardFailed('save', e);
	}
}

function *deleteV4Dashboard(user, id) {
	try {
		yield api.deleteV4Dashboard(user.v4ScopedAccountId, id);
		const rest = (yield select(selectDashboards())).filter(d => +d.id !== +id).map(d => d.toJS()).toArray();
		yield put(dataReceived('dashboards', rest));
		yield put(setSelectedDashboard(-1));
	} catch (e) {
		yield* v4DashboardFailed('delete', e);
	}
}

function *createDashboard({name}) {
	const user = yield select(selectCurrentUser());
	if (user?.isV4Native) {
		yield* createV4Dashboard(user, name);
		return;
	}
	const dashboard = yield api.create(name);
	yield put(setSelectedDashboard(dashboard.id));
}

function *deleteDashboard({id}) {
	const user = yield select(selectCurrentUser());
	if (user?.isV4Native) {
		yield* deleteV4Dashboard(user, id);
		return;
	}
	yield api.delete(id);
	yield put(setSelectedDashboard(-1));
}

function *saveSelectedDashboard({id}) {
	if(id > 0) localStorage.setItem('dashboard', id);
	else localStorage.removeItem('dashboard');
	yield Promise.resolve();
}

function *saveDashboard({name}) {
	let dashboard = yield select(selectEditDashboard());
	const user = yield select(selectCurrentUser());
	if (user?.isV4Native) {
		if (dashboard) yield* saveV4Dashboard(user, dashboard.set('name', name));
		else yield put(setLoading(false));
		return;
	}
	dashboard = dashboard.set('name', name);
	yield api.update(dashboard);
	yield all([
		put(setEditDashboard()),
		put(setLoading(false))
	]);
}
function *refreshPresentationPage({data}) {
	const page = yield select(selectOptions());
	if(page && +page.id === +data.id) {
		window.location.reload();
	}
}
function *refreshPresentationPage2({data}) {
	const page = yield select(selectOptions());
	if(page && page.get('dashboardIds').indexOf(+data.id) >= 0) {
		window.location.reload();
	}
}

function *subscribeWsTopics() {
	const user = yield select(selectCurrentUser());
	if(!user.isPresentation) return;
	const presentationPage = yield select(selectOptions());
	if(presentationPage) {
		const dashboards = yield api.getDashboards(presentationPage.get('dashboardIds'));
		const topics = _uniq(_flatten(dashboards.map(d => new Dashboard(d).wsTopics)));
		topics.push('dashboard');
		topics.push('dashboards');
		topics.map(topic => MyWebSocket.inst().subscribe(topic));
	} else if(['live-location', 'alarm-location'].indexOf(getActivePage()) >= 0) {
		MyWebSocket.inst().subscribe('*');
	}
	yield put(getData());
}

export const dashboardSagas = [
	takeLatest(CREATE_DASHBOARD, createDashboard),
	takeLatest(DELETE_DASHBOARD, deleteDashboard),
	takeLatest(SET_SELECTED_DASHBOARD, saveSelectedDashboard),
	takeLatest(SAVE_DASHBOARD, saveDashboard),
	takeLatest(WS_DATA_PRESENTATION_PAGE, refreshPresentationPage),
	takeLatest(WS_DATA_DASHBOARD, refreshPresentationPage2),
	takeLatest(APP_MOUNTED, subscribeWsTopics),
	takeLatest(AUTH_SUCCESS, subscribeWsTopics),
	takeLatest(SUBSCRIBE_WS_TOPICS, subscribeWsTopics),
	takeLatest(WS_DATA_INIT, loadV4Dashboards),
];